import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '../hooks/useDebounce';
import { formatDate as formatISTDate } from '../utils/datetime';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
// Using regular HTML table elements instead of UI components
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronUp,
  ChevronDown,
  Search,
  Eye,
  Download,
  Filter,
  Calendar,
  User,
  Settings,
  EyeOff,
  Eye as EyeIcon,
  QrCode,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Building,
  CreditCard,
  FileText,
  Image as ImageIcon,
  Printer,
  Award,
  Lock,
  Trash2,
  X
} from 'lucide-react';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import ImagePreviewModal from './ImagePreviewModal';
import ConfirmationDialog from './ui/ConfirmationDialog';
import RegistrationDetailsModal from './RegistrationDetailsModal';
import DynamicBadgePrintModal from './DynamicBadgePrintModal';
import { badgeShareAllowed } from '../utils/printWindow';
import LockerAssignModal from './LockerAssignModal';
import { useAuth } from '../contexts/AuthContext';
import { filterParamsFromSearch, filtersFromSearch, searchWithFilterParams, searchWithoutCondition, searchWithoutFilterParams } from '../utils/registrationFilterParams';
import RegistrationFilterBar from './RegistrationFilterBar';
import SearchBar from './SearchBar';
import { downloadInviteRegistrations } from '../utils/inviteExport';
import { resolveRoleUi } from '../utils/roleUi';

const INVITE_COLUMNS = ['invite.group', 'invite.maxAllowed', 'invite.used'];

// Ids and tokens that mean nothing to staff.
const INTERNAL_COLUMNS = new Set(['id', 'eventId', 'inviteLinkId', 'manageToken', 'couponHoldToken', 'couponId',
  'externalFaceId', 'faceEmbedding', 'userIdSearched', 'ticketPassId']);

const isImageFile = (file) => /^(jpe?g|png|gif|webp|bmp)$/i.test(file?.format || '')
  || /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(file?.fileName || '');
const isPdfFile = (file) => /pdf/i.test(file?.format || '') || /\.pdf(\?|$)/i.test(file?.fileName || '');

const fileButton = (file, openFile) => (
  <button
    type="button"
    key={file.url}
    onClick={() => openFile(file)}
    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
    title={file.fileName || 'View file'}
  >
    {isImageFile(file) && <img src={file.url} alt="" className="h-8 w-8 rounded object-cover" />}
    <Eye className="h-4 w-4" />
  </button>
);

const DynamicRegistrationsTable = ({ eventId, rfIdEnabled = false, lockerEnabled = false, showConfirmationEmailButton = true, onQueryChange, roleUi }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ui = roleUi || resolveRoleUi(null, user?.roles || []);
  // Deleting is admin-only on the API too.
  const canDelete = ui.isAdmin;
  const submitSearch = ui.searchMode === 'SUBMIT';
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  // Drill-down filters (e.g. from the analytics dashboard) arrive as URL params.
  const [filters, setFilters] = useState(() => filtersFromSearch(location.search).filters);
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // { userIds: [] }
  const [deleteReason, setDeleteReason] = useState('');
  const [dateRange, setDateRange] = useState({ 
    startDate: '', 
    endDate: '',
    startTime: '00:00',
    endTime: '23:59'
  });
  const [sorting, setSorting] = useState({ sortBy: 'createdAt', sortDirection: 'desc' });
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedKind, setSelectedKind] = useState('image');
  const [showImagePreview, setShowImagePreview] = useState(false);
  // Ordered list of visible column keys; null until the saved/default layout is resolved.
  const [visibleColumnsList, setVisibleColumnsList] = useState(null);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null);
  const [showRegistrationDetails, setShowRegistrationDetails] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showDynamicBadgePrint, setShowDynamicBadgePrint] = useState(false);
  const [badgePreviewData, setBadgePreviewData] = useState(null);
  const [selectedBadgeConfig, setSelectedBadgeConfig] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [assignRfidModalOpen, setAssignRfidModalOpen] = useState(false);
  const [rfidRegistration, setRfidRegistration] = useState(null);
  const [rfidValue, setRfidValue] = useState('');
  const [lockerModalUserId, setLockerModalUserId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const rfidInputRef = useRef(null);
  
  // Debounce search term
  const debouncedTypedTerm = useDebounce(searchTerm, 500);
  // Submitted searches run at once; typed ones wait for a pause.
  const activeSearchTerm = submitSearch ? searchTerm : debouncedTypedTerm;
  
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  // Helper function to format date with time
  const formatDateTime = (date, time) => {
    if (!date) return null;
    return `${date} ${time}`;
  };

  useEffect(() => {
    // The URL is the source of truth for these filters (set here or by an analytics drill-down).
    setFilters(filtersFromSearch(location.search).filters);
    setCurrentPage(0);
  }, [location.search]);

  const applyUrlFilters = (selections) => {
    const search = searchWithFilterParams(location.search, selections);
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  };

  useEffect(() => {
    onQueryChange?.({
      filters,
      searchTerm: activeSearchTerm,
      startDate: formatDateTime(dateRange.startDate, dateRange.startTime),
      endDate: formatDateTime(dateRange.endDate, dateRange.endTime),
    });
  }, [filters, activeSearchTerm, dateRange, onQueryChange]);

  const clearUrlFilters = () => {
    setFilters({});
    setCurrentPage(0);
    const rest = searchWithoutFilterParams(location.search);
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : '' }, { replace: true });
  };

  const deleteMutation = useMutation({
    mutationFn: ({ userIds, reason }) => (userIds.length === 1
      ? ApiService.deleteRegistration(eventId, userIds[0], reason)
      : ApiService.bulkDeleteRegistrations(eventId, userIds, reason)),
    onSuccess: (result, { userIds }) => {
      const deleted = result?.deleted ?? userIds.length;
      showSuccess(`Deleted ${deleted} registration${deleted === 1 ? '' : 's'}`);
      setSelectedUserIds(new Set());
      setDeleteTarget(null);
      setDeleteReason('');
      queryClient.invalidateQueries({ queryKey: ['registrations', eventId] });
      queryClient.invalidateQueries({ queryKey: ['qrScanStats', eventId] });
      queryClient.invalidateQueries({ queryKey: ['eventAnalytics', eventId] });
    },
    onError: (error) => showError(error.message || 'Failed to delete registrations'),
  });

  const toggleSelected = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  // Fetch registrations - always use filtered API
  const { data: registrationsResponse, isLoading, error } = useQuery({
    queryKey: ['registrations', eventId, currentPage, pageSize, activeSearchTerm, filters, dateRange, sorting],
    queryFn: () => ApiService.getFilteredRegistrations(eventId, {
      filters: filters,
      searchTerm: activeSearchTerm,
      page: currentPage,
      size: pageSize,
      sortBy: sorting.sortBy,
      sortDirection: sorting.sortDirection,
      startDate: formatDateTime(dateRange.startDate, dateRange.startTime),
      endDate: formatDateTime(dateRange.endDate, dateRange.endTime)
    }),
    enabled: !!eventId,
  });

  // Fetch QR scan purposes to check for badge/certificate configs
  const { data: qrScanPurposes = [] } = useQuery({
    queryKey: ['qrScanPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
    enabled: !!eventId,
  });

  const urlConditions = useMemo(() => {
    const purposeNames = Object.fromEntries(qrScanPurposes.map((p) => [p.purpose, p.name || p.purpose]));
    return filtersFromSearch(location.search, purposeNames).conditions;
  }, [location.search, qrScanPurposes]);

  const removeUrlCondition = (condition) => {
    const rest = searchWithoutCondition(location.search, condition);
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : '' }, { replace: true });
  };

  // Fetch event form schema for badge printing
  const { data: formSchema = [] } = useQuery({
    queryKey: ['eventFormSchema', eventId],
    queryFn: () => ApiService.getEvent(eventId).then(event => event.formSchema || []),
    enabled: !!eventId,
  });

  // Find badge and certificate purposes
  const badgePurpose = qrScanPurposes.find(p => p.shouldPrintBadge);
  // Check for certificate by purpose identifier or by shouldPrintCertificate flag
  const certificatePurpose = qrScanPurposes.find(p => 
    p.shouldPrintCertificate || 
    p.purpose === 'certificate' || 
    p.name?.toLowerCase().includes('certificate')
  );

  // Read purpose from URL (e.g. ?purpose=certificate) to influence hardware-scan flow
  const purposeFromUrl = (typeof window !== 'undefined')
    ? new URLSearchParams(window.location.search).get('purpose')
    : null;

  const registrations = registrationsResponse?.content || [];
  const totalElements = registrationsResponse?.totalElements || 0;
  const totalPages = registrationsResponse?.totalPages || 0;

  // Approve registration mutation
  const approveRegistrationMutation = useMutation({
    mutationFn: (userId) => ApiService.approveRegistration(eventId, userId, { reason: 'Manual approval by admin' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['registrations', eventId]);
      showSuccess('Registration approved successfully!');
      setShowApprovalDialog(false);
    },
    onError: (error) => {
      showError('Failed to approve registration. Please try again.');
    }
  });
  
  // Reject registration mutation
  const rejectRegistrationMutation = useMutation({
    mutationFn: ({ userId, reason }) => ApiService.rejectRegistration(eventId, userId, { reason: reason || 'Manual rejection by admin' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['registrations', eventId]);
      showSuccess('Registration rejected successfully!');
      setShowApprovalDialog(false);
      setRejectReason('');
    },
    onError: (error) => {
      showError('Failed to reject registration. Please try again.');
    }
  });
  
  // Assign RFID mutation
  const assignRfidMutation = useMutation({
    mutationFn: ({ registrationId, rfId }) =>
      ApiService.assignRfid(eventId, { registrationId, rfId }),
    onSuccess: () => {
      showSuccess('RFID assigned successfully!');
      queryClient.invalidateQueries(['registrations', eventId]);
      setAssignRfidModalOpen(false);
      setRfidRegistration(null);
      setRfidValue('');
    },
    onError: (error) => {
      console.error('Assign RFID failed:', error);
      showError(error?.message || 'Failed to assign RFID. Please try again.');
    },
  });
  
  const handleAssignRfid = (registration) => {
    const userId = registration?.userId || registration?.registrationId;
    if (!userId) {
      showError('Registration ID is missing for this record.');
      return;
    }
  
    setRfidRegistration({ userId });
    setRfidValue('');
    setAssignRfidModalOpen(true);
  };
  
  // Cancel registration mutation
  const cancelRegistrationMutation = useMutation({
    mutationFn: (userId) => ApiService.cancelRegistration(eventId, userId, { reason: 'Manual cancellation by admin' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['registrations', eventId]);
      showSuccess('Registration cancelled successfully!');
      setShowApprovalDialog(false);
    },
    onError: (error) => {
      showError('Failed to cancel registration. Please try again.');
    }
  });

  // Send registration confirmation email mutation
  const sendConfirmationEmailMutation = useMutation({
    mutationFn: (userId) => ApiService.sendRegistrationConfirmationEmails(eventId, [userId]),
    onSuccess: () => {
      showSuccess('Confirmation email sent successfully!');
    },
    onError: (error) => {
      showError('Failed to send confirmation email. Please try again.');
    }
  });

  // Autofocus RFID input when modal opens
  useEffect(() => {
    if (assignRfidModalOpen && rfidInputRef.current) {
      // Slight delay to ensure dialog is mounted
      setTimeout(() => {
        if (rfidInputRef.current) {
          rfidInputRef.current.focus();
        }
      }, 50);
    }
  }, [assignRfidModalOpen]);

  // Extract all possible columns from the data
  const allColumns = useMemo(() => {
    if (!registrations || registrations.length === 0) return [];
    
    const columns = new Set();
    let hasPendingApproval = false;
    
    registrations.forEach(registration => {
      // Check if any registration has PENDING_FOR_APPROVAL status
      if (registration.status === 'PENDING_FOR_APPROVAL') {
        hasPendingApproval = true;
      }
      
      // Add top-level fields (excluding nested objects)
      Object.keys(registration).forEach(key => {
        if (key !== 'formData' && key !== 'checkins' && typeof registration[key] !== 'object' && !INTERNAL_COLUMNS.has(key)) {
          columns.add(key);
        }
      });
      
      // Add formData fields (flatten the map)
      if (registration.formData && typeof registration.formData === 'object') {
        Object.keys(registration.formData).forEach(key => {
          columns.add(`formData.${key}`);
        });
      }
      
      // Add checkins fields
      if (registration.checkins && typeof registration.checkins === 'object') {
        Object.keys(registration.checkins).forEach(key => {
          columns.add(`checkins.${key}`);
        });
      }
    });
    
    // Always add actions column
    columns.add('actions');
    
    // Remove duplicates and sort (but keep actions at the end)
    const sortedColumns = Array.from(columns).filter(col => col !== 'actions').sort();
    sortedColumns.push('actions');
    
    return sortedColumns;
  }, [registrations]);

  const { data: eventDetails, isFetched: eventDetailsFetched } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
  });

  // Saved layout (per event) wins; otherwise the original default columns.
  useEffect(() => {
    // A failed event fetch falls back to the default layout rather than an empty table.
    if (visibleColumnsList || allColumns.length === 0 || !eventDetailsFetched) return;
    const saved = Array.isArray(eventDetails?.tableColumns) ? eventDetails.tableColumns : [];
    if (saved.length > 0) {
      setVisibleColumnsList(saved);
      return;
    }
    const defaultVisibleColumns = [
      'userId',
      'formData.Name',
      'formData.Category',
      'formData.email',
      'formData.Email',
      'formData.contact',
      'formData.Contact',
      'actions'
    ];
    setVisibleColumnsList(defaultVisibleColumns.filter((column) => allColumns.includes(column)));
  }, [allColumns, eventDetails, eventDetailsFetched, visibleColumnsList]);

  // Saved layouts may still list internal ids from before they were hidden.
  const shownColumns = useMemo(() => (visibleColumnsList || []).filter((c) => !INTERNAL_COLUMNS.has(c)), [visibleColumnsList]);

  // Invite link usage per row: group, its limit and how many registered (click downloads them).
  const { data: inviteStats = [] } = useQuery({
    queryKey: ['inviteLinks', eventId],
    queryFn: () => ApiService.getInviteLinks(eventId),
    enabled: !!eventId && ui.can('inviteLinks'),
    staleTime: 30 * 1000,
  });
  const invitesById = useMemo(() => Object.fromEntries(inviteStats.map(({ link }) => [link.id, link])), [inviteStats]);
  const hasInvites = inviteStats.some(({ link }) => link.usedCount > 0);
  const displayColumns = useMemo(() => (hasInvites
    ? [...shownColumns.filter((c) => !INVITE_COLUMNS.includes(c) && c !== 'inviteName'), ...INVITE_COLUMNS]
    : shownColumns), [hasInvites, shownColumns]);
  const [downloadingInvite, setDownloadingInvite] = useState(null);
  const downloadInvite = async (link) => {
    setDownloadingInvite(link.id);
    try {
      await downloadInviteRegistrations(eventId, link.id);
      showSuccess(`Downloading ${link.usedCount} registrations from ${link.name}`);
    } catch (err) {
      showError(err.message || 'Download failed');
    } finally {
      setDownloadingInvite(null);
    }
  };
  // Saved columns may not appear on the current page's rows; keep them selectable.
  const selectableColumns = useMemo(
    () => [...shownColumns, ...allColumns.filter((column) => !shownColumns.includes(column))],
    [allColumns, shownColumns]
  );

  const moveColumn = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= shownColumns.length) return;
    const next = [...shownColumns];
    [next[index], next[target]] = [next[target], next[index]];
    setVisibleColumnsList(next);
  };

  const saveColumnsMutation = useMutation({
    mutationFn: (columns) => ApiService.saveTableColumns(eventId, columns),
    onSuccess: (columns) => {
      queryClient.setQueryData(['event', eventId], (prev) => (prev ? { ...prev, tableColumns: columns } : prev));
      showSuccess('Column layout saved for this event');
    },
    onError: (error) => showError(error.message || 'Failed to save column layout'),
  });

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  // Handle search change
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(0); // Reset to first page when searching
  };

  // Toggle column visibility
  const toggleColumnVisibility = (column) => {
    setVisibleColumnsList((prev) => {
      const current = prev || [];
      return current.includes(column) ? current.filter((c) => c !== column) : [...current, column];
    });
  };

  // Handle approval/rejection actions
  const handleApprovalAction = (registration, action) => {
    setSelectedRegistration(registration);
    setApprovalAction(action);
    setShowApprovalDialog(true);
  };

  const confirmApprovalAction = () => {
    if (!selectedRegistration) return;
    
    if (approvalAction === 'approve') {
      approveRegistrationMutation.mutate(selectedRegistration.userId);
    } else if (approvalAction === 'reject') {
      rejectRegistrationMutation.mutate({ userId: selectedRegistration.userId, reason: rejectReason });
    } else if (approvalAction === 'cancel') {
      cancelRegistrationMutation.mutate(selectedRegistration.userId);
    }
  };

  const handleViewDetails = (userId) => {
    setSelectedUserId(userId);
    setShowRegistrationDetails(true);
  };

  const handleSendConfirmationEmail = (registration) => {
    if (!registration?.userId) {
      showError('Registration ID is missing for this record.');
      return;
    }
    sendConfirmationEmailMutation.mutate(registration.userId);
  };

  // Handle Print Badge
  const handlePrintBadge = async (registration) => {
    try {
      // First, call the scan API to get the QR code and updated registration data
      const scanData = await ApiService.scanQR(eventId, {
        qrData: registration.userId,
        purpose: badgePurpose?.purpose || 'badge',
        scanMode: 'manual',
        shouldPrintBadge: true,
        canUpdateUser: badgePurpose?.canUpdateUser || false
      });

      if(scanData.error) {
        console.error('Failed to scan QR:', scanData.error);
        return;
      }
      
    
      // Fetch badge config for the badge purpose
      const badgeConfig = await ApiService.getBadgeDesignerConfig(eventId, badgePurpose?.purpose || 'badge');
      
      // Prepare badge data with QR code from scan API
      const badgeData = {
        ...scanData.formData,
        userId: scanData.userId,
        qrCodeBase64: scanData.qrCodeBase64, // QR code from scan API
        purpose: badgePurpose?.name
      };
      
      // Update registration object with scan data
      const updatedRegistration = {
        ...registration,
        ...scanData,
        purposeConfig: badgePurpose
      };
      
      setBadgePreviewData(badgeData);
      setSelectedBadgeConfig(badgeConfig);
      setSelectedRegistration(updatedRegistration);
      setShowDynamicBadgePrint(true);
    } catch (error) {
      console.error('Failed to load badge config:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load badge configuration';
      showError(errorMessage);
    }
  };

  // Handle Print Certificate
  const handlePrintCertificate = async (registration) => {
    try {
      // First, call the scan API to get the QR code and updated registration data
      const scanData = await ApiService.scanQR(eventId, {
        qrData: registration.userId,
        purpose: certificatePurpose?.purpose || 'certificate',
        scanMode: 'manual',
        shouldPrintBadge: false,
        shouldPrintCertificate: true,
        canUpdateUser: certificatePurpose?.canUpdateUser || false
      });

      if(scanData.error) {
        console.error('Failed to scan QR:', scanData.error);
        return;
      }
      
      
    
      // Fetch certificate config
      const certificateConfig = await ApiService.getBadgeDesignerConfig(eventId, certificatePurpose?.purpose || 'certificate');
      
      // Prepare certificate data with QR code from scan API
      const certificateData = {
        ...scanData.formData,
        userId: scanData.userId,
        qrCodeBase64: scanData.qrCodeBase64, // QR code from scan API
        purpose: certificatePurpose?.name
      };
      
      // Update registration object with scan data
      const updatedRegistration = {
        ...registration,
        ...scanData,
        purposeConfig: certificatePurpose
      };
      
      setBadgePreviewData(certificateData);
      setSelectedBadgeConfig(certificateConfig);
      setSelectedRegistration(updatedRegistration);
      setShowDynamicBadgePrint(true);
    } catch (error) {
      console.error('Failed to load certificate config:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load certificate configuration';
      showError(errorMessage);
    }
  };

  // Handle hardware scanner input in search field
  const handleSearchChange = (value) => {
    setSearchInputValue(value);
    if (!submitSearch) {
      setSearchTerm(value);
      setCurrentPage(0);
    }
  };

  const handleSearchSubmit = (term) => {
    setSearchTerm(term);
    setCurrentPage(0);
  };

  const scannerMode = !!badgePurpose || (purposeFromUrl === 'certificate' && !!certificatePurpose);

  const handleSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    // Scanner input prints right away; plain searches submit through the form in submit mode.
    if (scannerMode || !submitSearch) {
      e.preventDefault();
      handleSearchKeyPress(e);
    }
  };

  // Handle hardware scanner - detect Enter key  
  const handleSearchKeyPress = async (e) => {
    if (e.key === 'Enter' && searchInputValue.trim()) {
      // Check if the input matches a userId directly (hardware scanner behavior)
      const matchedRegistration = registrations.find(
        reg => reg.userId?.toLowerCase() === searchInputValue.trim().toLowerCase()
      );

      console.log('searchInputValue', searchInputValue);
      console.log('registerations',registrations);
      console.log('matchedRegistration', matchedRegistration);
      const isCertificateMode = purposeFromUrl === 'certificate' && !!certificatePurpose;      
      if (matchedRegistration) {

        if (isCertificateMode) {
          await handlePrintCertificate(matchedRegistration);
        } else if (badgePurpose) {
          await handlePrintBadge(matchedRegistration);
        }

        // Clear search
        setSearchInputValue('');
        setSearchTerm('');
      } else {
        // No matching registration found
        // showError('No registration found with this ID');
        const customRegistration = {
          userId: searchInputValue.trim(),
        };

        if (isCertificateMode) {
          await handlePrintCertificate(customRegistration);
        } else if (badgePurpose) {
          await handlePrintBadge(customRegistration);
        }  
         // Clear search
         setSearchInputValue('');
         setSearchTerm('');
      }
    }
  };

  // Get nested value from object
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  };

  // Flatten formData object to show all key-value pairs
  const flattenFormData = (formData) => {
    if (!formData || typeof formData !== 'object') return null;
    
    const flattened = [];
    Object.entries(formData).forEach(([key, value]) => {
      flattened.push({ key, value });
    });
    return flattened;
  };

  // Format cell value
  const formatCellValue = (value, column, registration) => {
    if (INVITE_COLUMNS.includes(column)) {
      const link = invitesById[registration?.inviteLinkId];
      if (!link) return <span className="text-gray-400">-</span>;
      if (column === 'invite.group') return <span className="text-sm font-medium text-gray-900">{link.name}</span>;
      if (column === 'invite.maxAllowed') return <span className="text-sm tabular-nums">{link.maxUses ?? 'Unlimited'}</span>;
      return (
        <button
          type="button"
          onClick={() => downloadInvite(link)}
          disabled={downloadingInvite === link.id}
          className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-sm font-semibold tabular-nums text-blue-700 hover:bg-blue-100 disabled:opacity-60"
          title={`Download the ${link.usedCount} registrations from ${link.name} (Excel)`}
        >
          {link.usedCount}
          <Download className="h-3.5 w-3.5" />
        </button>
      );
    }
    // Handle actions column
    if (column === 'actions') {
      return (
        <div className="flex flex-wrap gap-1">
          {ui.can('viewDetails') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewDetails(registration.userId)}
              className="h-8 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Eye className="w-3 h-3 mr-1" />
              View Details
            </Button>
          )}

          {registration?.pendingChange && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewDetails(registration.userId)}
              className="h-8 px-2 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
            >
              Change request
            </Button>
          )}
          
          {/* Print Badge button - show if badge purpose exists */}
          {badgePurpose && ui.can('printBadge') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePrintBadge(registration)}
              className="h-8 px-2 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
            >
              <Printer className="w-3 h-3 mr-1" />
              Print Badge
            </Button>
          )}

          {/* Assign RFID button - only when RFID is enabled for the event */}
          {rfIdEnabled && ui.can('assignRfid') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAssignRfid(registration)}
              className="h-8 px-2 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
            >
              <User className="w-3 h-3 mr-1" />
              Assign RFID
            </Button>
          )}

          {/* Manage Lockers button - only when lockers are enabled for the event */}
          {lockerEnabled && ui.can('rowLockers') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLockerModalUserId(registration.userId)}
              className="h-8 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            >
              <Lock className="w-3 h-3 mr-1" />
              Lockers
            </Button>
          )}
          
          {/* Print Certificate button - show if certificate purpose exists */}
          {certificatePurpose && ui.can('printCertificate') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePrintCertificate(registration)}
              className="h-8 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
            >
              <Award className="w-3 h-3 mr-1" />
              Print Certificate
            </Button>
          )}
          
          {registration?.status === 'PENDING_FOR_APPROVAL' && ui.can('approveReject') && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleApprovalAction(registration, 'approve')}
                className="h-8 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Approve
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleApprovalAction(registration, 'reject')}
                className="h-8 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <XCircle className="w-3 h-3 mr-1" />
                Reject
              </Button>
            </>
          )}

          {showConfirmationEmailButton && ui.can('sendConfirmationEmail') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSendConfirmationEmail(registration)}
              disabled={sendConfirmationEmailMutation.isLoading}
              className="h-8 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Mail className="w-3 h-3 mr-1" />
              Send Confirmation Email
            </Button>
          )}

          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteTarget({ userIds: [registration.userId] })}
              className="h-8 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
          )}
        </div>
      );
    }

    if (value === null || value === undefined) {
      return <span className="text-gray-400">-</span>;
    }

    const fileFor = (item) => (typeof item === 'string' ? registration?.files?.[item] : null);
    const openFile = (file) => {
      const image = isImageFile(file);
      if (image || isPdfFile(file)) {
        setSelectedKind(image ? 'image' : 'pdf');
        setSelectedImage(file.url);
        setShowImagePreview(true);
        return;
      }
      window.open(file.url, '_blank', 'noopener,noreferrer');
    };
    if (Array.isArray(value)) {
      const files = value.map(fileFor).filter(Boolean);
      if (files.length > 0) {
        return <span className="inline-flex flex-wrap gap-2">{files.map((file) => fileButton(file, openFile))}</span>;
      }
    }
    const file = fileFor(value);
    if (file) return fileButton(file, openFile);

    // Handle different data types
    if (typeof value === 'boolean') {
      return (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return <span className="text-sm">{value.join(', ')}</span>;
      }
      
      // Handle nested objects
      if (column.startsWith('checkins.')) {
        const checkinType = column.split('.')[1];
        const checkinData = value;
        if (checkinData && checkinData.used) {
          return (
            <div className="flex items-center space-x-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600">
                {formatISTDate(checkinData.usedAt)}
              </span>
            </div>
          );
        } else {
          return (
            <div className="flex items-center space-x-1">
              <XCircle className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-400">Not used</span>
            </div>
          );
        }
      }
      
      return <span className="text-sm font-mono">{JSON.stringify(value)}</span>;
    }

    // Handle dates
    if (column.includes('At') || column.includes('Date')) {
      const formatted = formatISTDate(value, '');
      if (formatted) {
        return (
          <div className="flex items-center space-x-1">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm">{formatted}</span>
          </div>
        );
      }
    }

    // Handle URLs and QR codes
    if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('data:'))) {
      if (value.startsWith('data:image/')) {
        // Check if this is a QR code column
        if (column.includes('qrCode') || column.includes('QR')) {
          return (
            <div className="flex items-center space-x-2">
              <img 
                src={value} 
                alt="QR Code Preview"
                className="w-8 h-8 rounded object-cover cursor-pointer hover:opacity-80"
                onClick={() => {
                  setSelectedImage(value);
                  setShowImagePreview(true);
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedImage(value);
                  setShowImagePreview(true);
                }}
                className="h-6 px-2 text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                View QR
              </Button>
            </div>
          );
        } else {
          // Regular image
          return (
            <div className="flex items-center space-x-2">
              <img 
                src={value} 
                alt="Preview"
                className="w-8 h-8 rounded object-cover cursor-pointer hover:opacity-80"
                onClick={() => {
                  setSelectedImage(value);
                  setShowImagePreview(true);
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedImage(value);
                  setShowImagePreview(true);
                }}
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>
          );
        }
      } else {
        return (
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm underline"
          >
            View
          </a>
        );
      }
    }

    // Handle status fields
    if (column === 'status') {
      const getStatusBadge = (status) => {
        switch (status) {
          case 'REGISTERED':
            return 'bg-green-100 text-green-800';
          case 'PENDING_PAYMENT':
            return 'bg-yellow-100 text-yellow-800';
          case 'PAYMENT_FAILED':
            return 'bg-red-100 text-red-800';
          default:
            return 'bg-gray-100 text-gray-800';
        }
      };
      
      return (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(value)}`}>
          {value}
        </span>
      );
    }

    // Handle source fields
    if (column === 'source') {
      const getSourceBadge = (source) => {
        switch (source) {
          case 'ONLINE':
            return 'bg-blue-100 text-blue-800';
          case 'ON-SPOT':
            return 'bg-purple-100 text-purple-800';
          case 'BULK_UPLOAD':
            return 'bg-orange-100 text-orange-800';
          default:
            return 'bg-gray-100 text-gray-800';
        }
      };
      
      return (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getSourceBadge(value)}`}>
          {value}
        </span>
      );
    }

    // Handle formData fields specially
    if (column.startsWith('formData.')) {
      const fieldName = column.split('.')[1];
      return (
        <div className="text-sm">
          <div className="font-medium text-gray-900">{fieldName}</div>
          <div className="text-gray-600">{String(value)}</div>
        </div>
      );
    }

    // Default text formatting
    return <span className="text-sm">{String(value)}</span>;
  };

  // Get column display name
  const getColumnDisplayName = (column) => {
    const displayNames = {
      'userId': 'User ID',
      'status': 'Status',
      'registeredAt': 'Registered At',
      'source': 'Source',
      'paymentId': 'Payment ID',
      'qrCodeUrl': 'QR Code URL',
      'qrCodeBase64': 'QR Code',
      'invoiceUrl': 'Invoice URL',
      'actions': 'Actions',
      'formData.name': 'Name',
      'formData.Name': 'Name',
      'formData.email': 'Email',
      'formData.Email': 'Email',
      'formData.category': 'Category',
      'formData.Category': 'Category',
      'formData.company': 'Company',
      'formData.Company': 'Company',
      'formData.contact': 'Contact',
      'formData.Contact': 'Contact',
      'formData.phone': 'Phone',
      'formData.Phone': 'Phone',
      'checkins.badge': 'Badge Check-in',
      'checkins.checkin': 'Event Check-in',
      'inviteName': 'Invited By (link)',
      'invite.group': 'Invite group',
      'invite.maxAllowed': 'Max allowed',
      'invite.used': 'Used quantity',
      'inviteAccessCode': 'Access Code (link)',
      'ticketPassName': 'Pass',
      'bookingId': 'Booking ID',
      'currency': 'Currency',
    };
    
    if (displayNames[column]) return displayNames[column];
    // Answers are keyed by the question text; show it as written instead of "form Data …".
    if (column.startsWith('formData.')) return column.slice('formData.'.length).replace(/_/g, ' ');
    if (column.startsWith('checkins.')) return `Check-in: ${column.slice('checkins.'.length).replace(/_/g, ' ')}`;
    const words = column.replace(/([A-Z])/g, ' $1').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  };

  // Get column icon
  const getColumnIcon = (column) => {
    if (column.includes('email') || column.includes('Email')) return <Mail className="w-4 h-4" />;
    if (column.includes('phone') || column.includes('Phone') || column.includes('contact') || column.includes('Contact')) return <Phone className="w-4 h-4" />;
    if (column.includes('company') || column.includes('Company')) return <Building className="w-4 h-4" />;
    if (column.includes('At') || column.includes('Date')) return <Calendar className="w-4 h-4" />;
    if (column.includes('qrCode')) return <QrCode className="w-4 h-4" />;
    if (column.includes('payment')) return <CreditCard className="w-4 h-4" />;
    if (column.includes('invoice')) return <FileText className="w-4 h-4" />;
    if (column.includes('checkins')) return <CheckCircle className="w-4 h-4" />;
    return <User className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <SearchBar
              value={searchInputValue}
              onChange={handleSearchChange}
              onSubmit={handleSearchSubmit}
              onKeyDown={handleSearchKeyDown}
              mode={submitSearch ? 'SUBMIT' : 'AUTO'}
              placeholder={submitSearch
                ? 'Search name, email, phone, ID or any answer'
                : 'Search all fields... (Hardware scanner supported - press Enter)'}
              autoFocus
              className="md:flex-1"
            />
            {(ui.can('tableColumns') || ui.can('tableFilters')) && (
              <div className="flex items-center gap-2">
                {ui.can('tableColumns') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Columns
                  </Button>
                )}
                {ui.can('tableFilters') && (
                  <Button
                    variant={showFilters || urlConditions.length > 0 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filters{urlConditions.length > 0 ? ` (${urlConditions.length})` : ''}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className={showColumnSelector || showFilters ? '' : 'p-0'}>
          {/* Column Selector */}
          {showColumnSelector && ui.can('tableColumns') && (
            <div className="mb-4 p-4 border rounded-lg bg-gray-50 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="font-medium">Columns to display</h4>
                {canDelete && (
                  <Button
                    size="sm"
                    onClick={() => saveColumnsMutation.mutate(shownColumns)}
                    disabled={saveColumnsMutation.isPending || shownColumns.length === 0}
                  >
                    Save layout for this event
                  </Button>
                )}
              </div>
              {shownColumns.length > 0 && (
                <ol className="space-y-1">
                  {shownColumns.map((column, index) => (
                    <li key={column} className="flex items-center gap-2 rounded border bg-white px-2 py-1 text-sm">
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggleColumnVisibility(column)}
                        className="rounded border-gray-300"
                        aria-label={`Hide ${getColumnDisplayName(column)}`}
                      />
                      <span className="flex-1 text-gray-700">{getColumnDisplayName(column)}</span>
                      <Button variant="ghost" size="sm" onClick={() => moveColumn(index, -1)} disabled={index === 0} aria-label="Move up">
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => moveColumn(index, 1)} disabled={index === shownColumns.length - 1} aria-label="Move down">
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </li>
                  ))}
                </ol>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {selectableColumns.filter((column) => !shownColumns.includes(column)).map(column => (
                  <label key={column} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => toggleColumnVisibility(column)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-600">
                      {getColumnDisplayName(column)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {showFilters && ui.can('tableFilters') && (
            <RegistrationFilterBar
              value={filterParamsFromSearch(location.search)}
              onChange={applyUrlFilters}
              formSchema={formSchema}
              purposes={qrScanPurposes}
            />
          )}
        </CardContent>
      </Card>

      {/* Dynamic Table */}
      <Card>
        <CardContent className="p-0">
          {urlConditions.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-600">{urlConditions.length > 1 ? 'Matching all of' : 'Filtered by'}</span>
              {urlConditions.map((condition) => (
                <span key={condition.key} className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 pl-3 pr-1 py-1 text-blue-800">
                  {condition.label}
                  <button
                    type="button"
                    onClick={() => removeUrlCondition(condition)}
                    className="rounded-full p-0.5 hover:bg-blue-100"
                    aria-label={`Remove ${condition.label}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <Button variant="ghost" size="sm" onClick={clearUrlFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear all
              </Button>
            </div>
          )}

          {canDelete && selectedUserIds.size > 0 && (
            <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm">
              <span className="text-red-800">{selectedUserIds.size} selected</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelectedUserIds(new Set())}>Clear selection</Button>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => setDeleteTarget({ userIds: Array.from(selectedUserIds) })}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete selected
                </Button>
              </div>
            </div>
          )}

          {ui.listMode === 'SEARCH_ONLY' && !activeSearchTerm && Object.keys(filters || {}).length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
              <Search className="h-10 w-10 text-gray-300" />
              <p className="font-medium text-gray-800">Search to find an attendee</p>
              <p className="text-sm text-gray-500">Enter a name, email, phone or registration ID above.</p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600">Failed to load registrations</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    {canDelete && (
                      <th className="py-3 px-4 w-8">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          aria-label="Select all on this page"
                          checked={registrations.length > 0 && registrations.every((r) => selectedUserIds.has(r.userId))}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedUserIds((prev) => {
                              const next = new Set(prev);
                              registrations.forEach((r) => (checked ? next.add(r.userId) : next.delete(r.userId)));
                              return next;
                            });
                          }}
                        />
                      </th>
                    )}
                    {displayColumns.map((column) => (
                      <th key={column} className="text-left py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          {getColumnIcon(column)}
                          <span>{getColumnDisplayName(column)}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((registration) => (
                    <tr key={registration.userId} className="border-b hover:bg-gray-50">
                      {canDelete && (
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            aria-label={`Select ${registration.userId}`}
                            checked={selectedUserIds.has(registration.userId)}
                            onChange={() => toggleSelected(registration.userId)}
                          />
                        </td>
                      )}
                      {displayColumns.map((column) => (
                        <td key={column} className="py-3 px-4 whitespace-nowrap">
                          {formatCellValue(getNestedValue(registration, column), column, registration)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} registrations
              </span>
              <Select value={pageSize.toString()} onValueChange={(value) => handlePageSizeChange(parseInt(value))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Image Preview Modal */}
      {showImagePreview && selectedImage && (
        <ImagePreviewModal
          imageUrl={selectedImage}
          kind={selectedKind}
          onClose={() => {
            setShowImagePreview(false);
            setSelectedImage(null);
          }}
        />
      )}

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteReason('');
        }}
        onConfirm={() => deleteMutation.mutate({ userIds: deleteTarget.userIds, reason: deleteReason })}
        title={`Delete ${deleteTarget?.userIds.length === 1 ? 'registration' : `${deleteTarget?.userIds.length} registrations`}`}
        message="Deleted registrations are archived and removed from lists, counts, scan stats and analytics. Any lockers they hold are released."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        inputLabel="Reason (optional)"
        inputPlaceholder="e.g. duplicate registration"
        inputValue={deleteReason}
        onInputChange={setDeleteReason}
      />

      {/* Approval Confirmation Dialog */}
      {showApprovalDialog && selectedRegistration && (
        <ConfirmationDialog
          isOpen={showApprovalDialog}
          onClose={() => {
            setShowApprovalDialog(false);
            setSelectedRegistration(null);
            setApprovalAction(null);
            setRejectReason('');
          }}
          onConfirm={confirmApprovalAction}
          title={`${approvalAction === 'approve' ? 'Approve' : approvalAction === 'reject' ? 'Reject' : 'Cancel'} Registration`}
          message={`Are you sure you want to ${approvalAction} the registration for ${selectedRegistration?.userId}?`}
          confirmText={approvalAction === 'approve' ? 'Approve' : approvalAction === 'reject' ? 'Reject' : 'Cancel'}
          confirmVariant={approvalAction === 'approve' ? 'default' : 'destructive'}
          isLoading={rejectRegistrationMutation.isPending || approveRegistrationMutation.isPending}
          {...(approvalAction === 'reject' && {
            inputLabel: 'Rejection Reason (optional)',
            inputPlaceholder: 'Enter reason for rejection…',
            inputValue: rejectReason,
            onInputChange: setRejectReason,
          })}
        />
      )}

      {/* Registration Details Modal */}
      <RegistrationDetailsModal
        isOpen={showRegistrationDetails}
        onClose={() => {
          setShowRegistrationDetails(false);
          setSelectedUserId(null);
        }}
        eventId={eventId}
        userId={selectedUserId}
        onStatusChanged={() => {
          queryClient.invalidateQueries(['registrations', eventId]);
        }}
      />

      {/* Dynamic Badge Print Modal */}
      {showDynamicBadgePrint && selectedBadgeConfig && badgePreviewData && (
        <DynamicBadgePrintModal
          isOpen={showDynamicBadgePrint}
          onClose={() => {
            setShowDynamicBadgePrint(false);
            setBadgePreviewData(null);
            setSelectedBadgeConfig(null);
            setSelectedRegistration(null);
          }}
          badgeConfig={selectedBadgeConfig}
          userData={badgePreviewData}
          eventId={eventId}
          scannedUser={selectedRegistration}
          formSchema={formSchema}
          rfIdEnabled={rfIdEnabled}
          lockerEnabled={lockerEnabled}
          shareEnabled={badgeShareAllowed(eventDetails)}
          onAssignRfid={handleAssignRfid}
          onUserDataUpdate={async (userId, formData) => {
            // Update user data via API
            await ApiService.updateRegistration(eventId, userId, formData);
            // Refresh registrations table
            queryClient.invalidateQueries(['registrations', eventId]);
            // Update badge preview data
            setBadgePreviewData({
              ...badgePreviewData,
              ...formData
            });
          }}
        />
      )}

      {/* Assign RFID Modal */}
      <Dialog
        open={assignRfidModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAssignRfidModalOpen(false);
            setRfidRegistration(null);
            setRfidValue('');
          }
        }}
      >
        <DialogContent className="max-w-md w-full p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Assign RFID</DialogTitle>
            <DialogDescription>
              {rfidRegistration?.userId
                ? `Scan or enter RFID for registration ${rfidRegistration.userId}.`
                : 'Scan or enter RFID for this registration.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-2">
            <Label htmlFor="rfid-input">RFID</Label>
            <Input
              id="rfid-input"
              ref={rfidInputRef}
              value={rfidValue}
              onChange={(e) => setRfidValue(e.target.value)}
              placeholder="Place card on reader or type RFID manually"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!rfidRegistration?.userId) return;
                  if (!rfidValue.trim()) {
                    showError('Please scan or enter an RFID value.');
                    return;
                  }
                  assignRfidMutation.mutate({
                    registrationId: rfidRegistration.userId,
                    rfId: rfidValue.trim(),
                  });
                }
              }}
            />
            <p className="text-xs text-gray-500">
              Focus is automatically set here so your RFID reader can type directly.
            </p>
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAssignRfidModalOpen(false);
                setRfidRegistration(null);
                setRfidValue('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!rfidRegistration?.userId) return;
                if (!rfidValue.trim()) {
                  showError('Please scan or enter an RFID value.');
                  return;
                }
                assignRfidMutation.mutate({
                  registrationId: rfidRegistration.userId,
                  rfId: rfidValue.trim(),
                });
              }}
              disabled={assignRfidMutation.isPending}
            >
              {assignRfidMutation.isPending ? 'Assigning...' : 'Assign RFID'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Locker Management Modal */}
      <LockerAssignModal
        open={!!lockerModalUserId}
        onClose={() => setLockerModalUserId(null)}
        eventId={eventId}
        userId={lockerModalUserId}
      />
    </div>
  );
};

export default DynamicRegistrationsTable;